export type Volume = {
  number: string
  title: string
  classes?: string
  subtitle: string
  problem: string[]
  extraList?: { label: string; items: string[] }
  listLabel: string
  bullets: string[]
  flow?: { label?: string; steps: string[] }
  highlight?: { label?: string; items: string[] }
  result: string[]
}

export const volumes: Volume[] = [
  {
    number: 'VOLUME 1',
    title: 'Stretching Foundations & Essentials',
    subtitle:
      'Build Strong Stretching Foundations Before Adding Greater Range or Complexity',
    problem: [
      'Good stretching instruction is not simply asking someone to “stretch further.”',
      'Clients need clear positioning, controlled breathing, appropriate range, stable posture and a progression they can understand.',
      'This volume establishes the foundation behind safe, structured and professional stretching classes.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Stretching fundamentals',
      'Positioning and alignment',
      'Breathing',
      'Range awareness',
      'Basic static stretching',
      'Introductory mobility',
      'Hold-time guidance',
      'Transition fundamentals',
      'Full-body beginner sequences',
      'Instructor cueing',
      'Common errors',
      'Beginner modifications',
      'Progression principles',
      'Ready-to-teach foundation classes',
    ],
    flow: {
      label: '100% Visual Core Format — See:',
      steps: ['Start', 'Position', 'Stretch', 'Hold', 'Release', 'Reset'],
    },
    result: [
      'You can teach foundational stretching with a repeatable system instead of simply giving clients a collection of random stretches.',
    ],
  },
  {
    number: 'VOLUME 2',
    title: 'Lower Body Flexibility & Mobility',
    subtitle:
      'Build Better Hips, Legs and Lower-Body Mobility Without Repeating the Same Routine',
    problem: [
      'Lower-body stretching is one of the most requested areas in flexibility classes.',
      'But repeatedly using the same hamstring, hip and calf stretches makes sessions predictable.',
      'This volume gives you structured lower-body classes with different objectives, sequences and progression options.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Hamstrings',
      'Hip flexors',
      'Quadriceps',
      'Glutes',
      'Adductors',
      'Abductors',
      'Calves',
      'Ankles',
      'Hip rotation',
      'Lower-body mobility',
      'Standing stretches',
      'Floor-based stretches',
      'Unilateral work',
      'Full lower-body sequences',
      'Beginner modifications',
      'Advanced progressions',
      'Instructor cues',
      'Common corrections',
    ],
    result: [
      'You gain more lower-body variety without having to reinvent your programming every week.',
    ],
  },
  {
    number: 'VOLUME 3',
    title: 'Upper Body Flexibility & Mobility',
    subtitle:
      'Give the Upper Body the Same Structure as Your Lower-Body Programming',
    problem: [
      'Upper-body mobility is often reduced to a few shoulder and neck stretches at the end of class.',
      'This volume gives you a structured system for developing upper-body flexibility and mobility with greater purpose.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Neck mobility',
      'Shoulders',
      'Chest',
      'Upper back',
      'Thoracic mobility',
      'Lats',
      'Triceps',
      'Biceps',
      'Wrists and forearms',
      'Postural mobility',
      'Rotation',
      'Extension',
      'Upper-body sequences',
      'Full-body integration',
      'Modifications',
      'Progressions',
      'Common errors and corrections',
    ],
    result: [
      'Upper-body work becomes a complete part of the class instead of a quick add-on at the end.',
    ],
  },
  {
    number: 'VOLUME 4',
    title: 'Dynamic Stretching & Active Mobility',
    subtitle: 'Add Movement Without Losing Control',
    problem: [
      'Not every stretching class should be completely static.',
      'Dynamic stretching and active mobility help instructors prepare clients for movement, create more energetic sessions and improve control through range.',
      'This volume teaches structured active flexibility rather than random bouncing or uncontrolled movement.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Dynamic mobility',
      'Controlled leg swings',
      'Active hip mobility',
      'Shoulder mobility',
      'Rotational movement',
      'Dynamic hamstring work',
      'Walking mobility sequences',
      'Active range development',
      'Warm-up flows',
      'Full-body mobility sequences',
      'Controlled transitions',
      'Movement preparation',
      'Modifications',
      'Progressions',
      'Ready-to-teach dynamic classes',
    ],
    result: [
      'You can add movement, energy and active flexibility while maintaining structure and control.',
    ],
  },
  {
    number: 'VOLUME 5',
    title: 'Flexibility for Health & Performance',
    subtitle: 'Connect Flexibility Work to Real Movement Goals',
    problem: [
      'Stretching is more useful when the client understands why the movement matters.',
      'This volume organizes flexibility work around practical objectives such as movement quality, recovery, mobility and physical performance.',
    ],
    listLabel: 'Inside:',
    bullets: [
      'Full-body mobility',
      'Recovery-focused stretching',
      'Posture-focused sessions',
      'Movement preparation',
      'Range-of-motion development',
      'Flexibility for active clients',
      'Mobility for daily movement',
      'Controlled recovery sessions',
      'Full-body resets',
      'Lower-body recovery',
      'Upper-body recovery',
      'Progressive flexibility',
      'Mixed mobility classes',
      'Instructor decision-making',
      'Class adaptations',
    ],
    result: [
      'Your classes move beyond isolated stretches and become purposeful flexibility sessions with clear objectives.',
    ],
  },
  {
    number: 'VOLUME 6',
    title: 'Progressive Flexibility & Multi-Level Programming',
    subtitle: 'Stop Teaching Isolated Classes and Start Creating Real Progression',
    problem: [
      'Clients should not attend random stretching classes forever.',
      'They should be able to feel their control, comfort, mobility and usable range improving over time.',
      'This volume helps you organize progression from beginner to more advanced flexibility work.',
    ],
    extraList: {
      label: 'Choose According To:',
      items: [
        'Current flexibility level',
        'Movement confidence',
        'Mobility restrictions',
        'Class objective',
        'Client experience',
        'Range tolerance',
        'Available equipment',
        'Progression stage',
        'Available teaching time',
      ],
    },
    listLabel: 'Classes Develop:',
    bullets: [
      'Better movement control',
      'Greater usable range',
      'More challenging positions',
      'Longer controlled holds',
      'Active flexibility',
      'More complex transitions',
      'Full-body integration',
      'Improved mobility',
      'Greater independence',
      'Multi-level class participation',
    ],
    flow: { steps: ['Open', 'Choose', 'Teach', 'Progress'] },
    result: [
      'Instead of asking yourself “What should come next?”, you already have a progression framework ready to use.',
    ],
  },
]
